const DEV_URLS = {
    reservation: {
        url: 'https://dev-reservation.tracepit.jp/',
        ready: true,
    },
    fuel: {
        url: 'https://dev-fuel.tracepit.jp/',
        ready: true,
    },
    richmenu: {
        url: '#',
        ready: false,
    },
    promo: {
        url: 'https://dev-promo.tracepit.jp/',
        ready: true,
    },
    'saas-admin': {
        url: 'https://dev-console.tracepit.jp/',
        ready: true,
    },
};
const PROD_URLS = {
    reservation: {
        url: 'https://reservation.tracepit.jp/',
        ready: true,
    },
    fuel: {
        url: 'https://fuel.tracepit.jp/',
        ready: true,
    },
    richmenu: {
        url: '#',
        ready: false,
    },
    promo: {
        // TP-33 で独自サブドメイン割当（旧 *.web.app は cross-site で prerender 対象外だった）。
        url: 'https://promo.tracepit.jp/',
        ready: true,
    },
    'saas-admin': {
        // saas-admin はテナント向け admin と混同しない `console` を採用。
        url: 'https://console.tracepit.jp/',
        ready: true,
    },
};
/**
 * 環境（dev / prod）を判定して URL map を返す。
 * - vitest は development 扱い（`mode === 'test'` のときも dev を返す）
 */
function pickUrlMap(mode) {
    return mode === 'production' ? PROD_URLS : DEV_URLS;
}
/** 機能 app の URL + ready を解決する純関数（test 用に mode を直接渡せる）。 */
export function resolveFeatureAppUrl(key, mode) {
    return pickUrlMap(mode)[key];
}
/**
 * 各管理アプリのヘッダーに並べる相互リンク（左→右の表示順）。
 * richmenu は領域確保のみで実装が無いため含めない。
 */
export const MANAGEMENT_APP_LINKS = [
    { feature: 'reservation', label: 'LINE予約' },
    { feature: 'fuel', label: '燃料価格' },
    { feature: 'promo', label: '顧客/販促' },
];
/**
 * ヘッダーに表示する管理画面リンク一覧を解決する純関数。
 *
 * - 現在の app（`currentApp`）は契約有無に関わらず常に含める（active 表示で現在地を示す）
 * - それ以外は「契約済み（`features` に含む）かつ ready」のものだけ含める
 * - S-90: tenant-portal も同じタブバーを描画するため `currentApp: null` を許容。
 *   null のときは全リンクを「契約済み && ready」でフィルタし、全て `isCurrent: false`
 *   （現在地は AppShell が portalUrl から自前描画する「ホーム」タブ）
 *
 * @param features  サインイン中ユーザーの契約機能（`useAuth().features`）
 * @param currentApp このヘッダーを描画している app の機能 key（portal は null）
 * @param mode      `import.meta.env.MODE`（'production' / それ以外）
 */
export function resolveManagementAppLinks(features, currentApp, mode) {
    return MANAGEMENT_APP_LINKS.filter((link) => {
        if (link.feature === currentApp)
            return true;
        return (features.includes(link.feature) && resolveFeatureAppUrl(link.feature, mode).ready);
    }).map((link) => ({
        key: link.feature,
        feature: link.feature,
        label: link.label,
        url: resolveFeatureAppUrl(link.feature, mode).url,
        isCurrent: link.feature === currentApp,
    }));
}
/**
 * M-164: 上部タブ「イベント」の遷移先パス（tenant-promo 内のルート / 抽選状況）。
 * tenant-promo の `ROUTES.eventStatus` と同じ値でなければならない（promo 側の routes
 * テストで突合する）。
 */
export const EVENT_TAB_PATH = '/event/status';
/** 上部タブ「イベント」のキー（`ResolvedAppLink.key`）。 */
export const EVENT_TAB_KEY = 'event';
/**
 * M-164: tenant-promo 以外の管理画面（LINE予約 / 燃料価格 / ポータル）の上部タブに並べる
 * 「イベント」リンクを解決する純関数。
 *
 * イベント（ルーレット抽選）の管理画面は tenant-promo 内のタブ（`/event/*`）だが、
 * 他の管理画面からは「顧客/販促 → イベント」の 2 段を踏まないと辿れなかった。promo と同じ
 * オリジンへの SSO ハンドオーバー（`feature: 'promo'`）で `/event/status` に直接着地させる。
 *
 * - promo が契約済み && ready のときだけ返す（未契約は null）
 * - 位置は「顧客/販促」の右（呼出側が `resolveManagementAppLinks` の結果に続けて並べる）
 * - tenant-promo 自身は同一 SPA 内タブ（`InAppTab`）で描くため本関数を使わない
 * - 確立記録 / トークンキャッシュは feature 単位なので「顧客/販促」と共有される
 *   （同一オリジン = 一度確立すればどちらも素リンク遷移）
 */
export function resolveEventAppLink(features, mode) {
    if (!features.includes('promo'))
        return null;
    const promo = resolveFeatureAppUrl('promo', mode);
    if (!promo.ready)
        return null;
    return {
        key: EVENT_TAB_KEY,
        feature: 'promo',
        label: 'イベント',
        url: new URL(EVENT_TAB_PATH, promo.url).toString(),
        isCurrent: false,
    };
}
/**
 * M-164: tenant-promo 以外の管理画面の上部タブ一式（管理画面リンク + 「イベント」）。
 * `currentApp` は tenant-promo 以外（portal は null）。
 */
export function resolveHeaderAppLinks(features, currentApp, mode) {
    const links = resolveManagementAppLinks(features, currentApp, mode);
    const event = resolveEventAppLink(features, mode);
    return event ? [...links, event] : links;
}
/**
 * A-39: tenant-portal（テナント管理者ハブ）の URL（dev / prod）。
 * 機能パッケージではないため FeatureAppKey とは別管理。各管理アプリのヘッダー
 * 「ホーム」ボタンの遷移先。ポータルは自オリジンに自前の Auth セッションを保持するため、
 * SSO ハンドオーバー不要の素のリンク遷移で戻れる（SSO は portal → 各 app の一方向）。
 */
const PORTAL_URLS = {
    development: 'https://dev-portal.tracepit.jp/',
    production: 'https://portal.tracepit.jp/',
};
/** tenant-portal の URL を解決する純関数（mode は `import.meta.env.MODE` を渡す）。 */
export function resolvePortalUrl(mode) {
    return mode === 'production' ? PORTAL_URLS.production : PORTAL_URLS.development;
}
/** `.web.app` ホストに `.firebaseapp.com` ミラーを併記して展開する（両方で配信されるため）。 */
function withFirebaseappMirror(hosts) {
    return hosts.flatMap((h) => h.endsWith('.web.app') ? [h, h.replace(/\.web\.app$/, '.firebaseapp.com')] : [h]);
}
/**
 * TP-33: 旧ドメイン（並行稼働）の app 別ホスト一覧（`.web.app` と `.firebaseapp.com`
 * の両ミラー + apollostation カスタムドメイン）。
 *
 * 各 app の main.tsx が boot 時にこのホストを検出したら正規ドメインへ `location.replace`
 * する（`resolveCanonicalAdminUrl`）。旧ブックマークの利用者にも same-site 化（prerender /
 * SSO 確立記録）の恩恵を届け、旧オリジンにセッションや localStorage 記録が蓄積し続けるのを止める。
 *
 * exact match のみ（Hosting preview channel の `*--preview-*.web.app` 等は対象外）。
 */
export const LEGACY_ADMIN_HOSTS = {
    portal: withFirebaseappMirror([
        'takefu21ss-tenant-portal-dev.web.app',
        'takefu21ss-tenant-portal-prod.web.app',
        'tenant-portal.apollostation-takefu21ss.com',
    ]),
    reservation: withFirebaseappMirror([
        'kawai-reserve-admin-dev.web.app',
        'kawai-reserve-admin.web.app',
        'admin-reserve.apollostation-takefu21ss.com',
    ]),
    fuel: withFirebaseappMirror([
        'takefu21ss-tenant-fuel-dev.web.app',
        'takefu21ss-tenant-fuel-prod.web.app',
        'tenant-fuel.apollostation-takefu21ss.com',
    ]),
    promo: withFirebaseappMirror([
        'takefu21ss-tenant-promo-dev.web.app',
        'takefu21ss-tenant-promo-prod.web.app',
    ]),
    'saas-admin': withFirebaseappMirror([
        'takefu21ss-saas-admin-dev.web.app',
        'takefu21ss-saas-admin-prod.web.app',
        'saas-admin.apollostation-takefu21ss.com',
    ]),
};
/**
 * TP-33: 現在のホストが app の旧ドメインなら、引っ越し先の正規 URL を返す純関数。
 * 正規ホスト・未知ホスト（localhost / Hosting preview channel 等）は null（リダイレクト不要）。
 *
 * mode 非依存で判定する: 旧 dev ホストには dev ビルド（mode=development）、旧 prod ホストには
 * prod ビルドが配信されるため、`resolveFeatureAppUrl(app, mode)` がそのまま正しい行き先になる。
 */
export function resolveCanonicalAdminUrl(app, currentHost, mode) {
    if (!LEGACY_ADMIN_HOSTS[app].includes(currentHost))
        return null;
    return app === 'portal' ? resolvePortalUrl(mode) : resolveFeatureAppUrl(app, mode).url;
}
//# sourceMappingURL=featureAppUrls.js.map